(() => {
  const EMPTY_VALUE = "__bh_oem_unspecified__";
  const SELECT_ORDER = [
    "make",
    "model",
    "generation",
    "year",
    "engineName",
    "tcu",
  ];
  const URL_KEYS = {
    make: "oem_make",
    model: "oem_model",
    generation: "oem_generation",
    year: "oem_year",
    engineName: "oem_engine",
    tcu: "oem_tcu",
    application: "oem_application",
    option: "oem_option",
    packageType: "oem_package",
  };
  const PACKAGE_TYPES = [
    ["ecu", "priceEcu"],
    ["tcu", "priceTcu"],
    ["ecuTcuPackage", "pricePackage"],
  ];
  const ATTRIBUTION_FIELDS = [
    ["utmSource", "utm_source"],
    ["utmMedium", "utm_medium"],
    ["utmCampaign", "utm_campaign"],
    ["utmContent", "utm_content"],
    ["utmTerm", "utm_term"],
    ["placement", "placement"],
    ["fbclid", "fbclid"],
    ["landingPage", "landing_page"],
  ];
  const ATTRIBUTION_STORAGE_KEY = "boosthaus_oem_attribution";
  const SUCCESS_REDIRECT_PATH = "/pages/oem-thank-you?submitted=1";
  const DATA_REQUESTS = new Map();
  let confirmedSubmissionHandled = false;

  function requestData(dataUrl) {
    if (!DATA_REQUESTS.has(dataUrl)) {
      const request = fetch(dataUrl, {
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `OEM+ data request failed with ${response.status}`,
            );
          }
          return response.json();
        })
        .catch((error) => {
          DATA_REQUESTS.delete(dataUrl);
          throw error;
        });
      DATA_REQUESTS.set(dataUrl, request);
    }

    return DATA_REQUESTS.get(dataUrl);
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) {
      element.className = className;
    }
    if (text !== undefined && text !== null) {
      element.textContent = String(text);
    }
    return element;
  }

  function uniqueSorted(values) {
    return [...new Set(values)].sort((left, right) =>
      String(left).localeCompare(String(right), "bg", {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }

  class BoostHausOemConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.data = null;
      this.loadingPromise = null;
      this.restoring = false;
      this.lastTrackedVehicle = "";
      this.lastTrackedResult = "";
      this.configuratorStarted = false;
      this.formStarted = false;
      this.formInView = false;
      this.stickyState = "";
      this.state = Object.fromEntries(SELECT_ORDER.map((key) => [key, ""]));
      this.selected = {
        applicationId: "",
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.labels = this.readLabels();
      this.showPrices = this.dataset.showPrices === "true";
      this.showOutputs = this.dataset.showOutputs === "true";
      this.showSource = this.dataset.showSource === "true";
      this.selects = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-select]")].map((select) => {
          select.dataset.placeholder =
            select.options[0]?.textContent ?? this.labels.choose;
          return [select.dataset.bhOemSelect, select];
        }),
      );
      this.fields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-field]")].map((field) => [
          field.dataset.bhOemField,
          field,
        ]),
      );
      this.status = this.querySelector("[data-bh-oem-status]");
      this.statusText = this.querySelector("[data-bh-oem-status-text]");
      this.fieldsContainer = this.querySelector("[data-bh-oem-fields]");
      this.retryButton = this.querySelector("[data-bh-oem-retry]");
      this.clearButton = this.querySelector("[data-bh-oem-clear]");
      this.results = this.querySelector("[data-bh-oem-results]");
      this.resultsTitle = this.querySelector("[data-bh-oem-results-title]");
      this.resultsGrid = this.querySelector("[data-bh-oem-results-grid]");
      this.empty = this.querySelector("[data-bh-oem-empty]");
      this.emptyTitle = this.querySelector("[data-bh-oem-empty-title]");
      this.emptyText = this.querySelector("[data-bh-oem-empty-text]");
      this.summary = this.querySelector("[data-bh-oem-summary-value]");
      this.summary.dataset.defaultText = this.summary.textContent.trim();
      this.summaryDetails = this.querySelector(
        "[data-bh-oem-summary-details]",
      );
      this.summaryFields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-summary-field]")].map(
          (field) => [field.dataset.bhOemSummaryField, field],
        ),
      );
      this.hiddenFields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-hidden]")].map((input) => [
          input.dataset.bhOemHidden,
          input,
        ]),
      );
      this.progressItems = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-progress]")].map((item) => [
          item.dataset.bhOemProgress,
          item,
        ]),
      );
      this.loadFallback = this.querySelector("[data-bh-oem-load-fallback]");
      this.selectorSection = this.querySelector(".bh-oem-selector");
      this.formSection = this.querySelector(".bh-oem-lead");
      this.editButton = this.querySelector("[data-bh-oem-edit]");
      this.resultsEditButton = this.querySelector(
        "[data-bh-oem-edit-results]",
      );
      this.form = this.querySelector(".bh-oem-form");
      this.emailInput = this.querySelector('input[name="email"]');
      this.emailError = this.querySelector("[data-bh-oem-email-error]");
      this.formError = this.querySelector("[data-bh-oem-form-error]");
      this.formSubject = this.querySelector("[data-bh-oem-form-subject]");
      this.formSubjectDefault = this.formSubject?.value.trim() || "OEM+ enquiry";
      this.formSubmit = this.querySelector("[data-bh-oem-form-submit]");
      this.formSubmitDefaultText = this.formSubmit?.textContent.trim() || "";
      this.sticky = this.querySelector("[data-bh-oem-sticky]");
      this.stickySummary = this.querySelector(
        "[data-bh-oem-sticky-summary]",
      );
      this.stickyDetail = this.querySelector(
        "[data-bh-oem-sticky-detail]",
      );
      this.stickyAction = this.querySelector(
        "[data-bh-oem-sticky-action]",
      );
      if (this.editButton) {
        this.editButton.hidden = true;
      }

      for (const [key, select] of Object.entries(this.selects)) {
        select.addEventListener("pointerdown", () =>
          this.trackConfiguratorStart(),
        );
        select.addEventListener("keydown", () =>
          this.trackConfiguratorStart(),
        );
        select.addEventListener("change", () => {
          this.trackConfiguratorStart();
          this.handleChange(key);
        });
      }

      this.retryButton?.addEventListener("click", () =>
        this.loadData({ restore: this.hasRestorableUrlState() }),
      );
      this.clearButton?.addEventListener("click", () => this.clearSelection());
      this.editButton?.addEventListener("click", () => this.editSelection());
      this.resultsEditButton?.addEventListener("click", () =>
        this.editSelection(),
      );
      this.querySelectorAll("[data-bh-oem-primary]").forEach((control) => {
        control.addEventListener("click", (event) => {
          event.preventDefault();
          this.publish("boosthaus_oem_primary_offer_click", {
            event_source: "oem_configurator",
            cta_location: control.dataset.ctaLocation || "page",
            ...this.analyticsSelection(),
          });
          this.continueJourney();
        });
      });
      this.querySelectorAll("[data-bh-oem-manual]").forEach((control) => {
        control.addEventListener("click", () => this.selectManualInquiry());
      });
      this.querySelectorAll("[data-bh-oem-viber]").forEach((link) => {
        link.addEventListener("click", () => {
          this.publish("boosthaus_oem_viber_click", {
            event_source: "oem_configurator",
            cta_location: link.closest(".bh-oem-hero")
              ? "hero"
              : link.closest(".bh-oem-lead")
                ? "form"
                : "fallback",
            ...this.analyticsSelection(),
          });
        });
      });
      this.emailInput?.addEventListener("invalid", () =>
        this.showEmailError(),
      );
      this.emailInput?.addEventListener("input", () => {
        if (this.emailInput.validity.valid) {
          this.hideEmailError();
        }
      });
      this.form?.addEventListener("submit", (event) =>
        this.handleFormSubmit(event),
      );
      this.form?.addEventListener(
        "focusin",
        () => {
          if (this.formStarted) {
            return;
          }
          this.formStarted = true;
          this.publish("boosthaus_oem_enquiry_form_started", {
            event_source: "oem_configurator",
            ...this.analyticsSelection(),
          });
        },
        { once: true },
      );
      this.stickyAction?.addEventListener("click", () =>
        this.handleStickyAction(),
      );
      if ("IntersectionObserver" in window && this.formSection) {
        this.formVisibilityObserver = new IntersectionObserver(
          (entries) => {
            this.formInView = entries.some(
              (entry) => entry.isIntersecting && entry.intersectionRatio > 0.08,
            );
            this.updateStickyCta();
          },
          { threshold: [0, 0.08, 0.25] },
        );
        this.formVisibilityObserver.observe(this.formSection);
      }
      window.addEventListener("resize", () => this.updateStickyCta());
      this.captureAttribution();
      this.updateViberLinks();
      this.setStatus(this.labels.loading, "loading");
      this.updateProgress();
      this.publish("boosthaus_oem_page_viewed", {
        event_source: "oem_configurator",
        device_context: this.deviceContext(),
      });

      this.loadData({
        restore: this.hasRestorableUrlState(),
      });
    }

    readLabels() {
      return {
        idle: this.dataset.labelIdle,
        loading: this.dataset.labelLoading,
        ready: this.dataset.labelReady,
        loadError: this.dataset.labelLoadError,
        retry: this.dataset.labelRetry,
        restored: this.dataset.labelRestored,
        restoreIgnored: this.dataset.labelRestoreIgnored,
        choose: this.dataset.labelChoose,
        unknown: this.dataset.labelUnknown,
        unspecifiedYear: this.dataset.labelUnspecifiedYear,
        unspecifiedGeneration: this.dataset.labelUnspecifiedGeneration,
        unspecifiedEngine: this.dataset.labelUnspecifiedEngine,
        uncertainTcu: this.dataset.labelUncertainTcu,
        resultSingular: this.dataset.labelResultSingular,
        resultPlural: this.dataset.labelResultPlural,
        resultsFoundSingle: this.dataset.labelResultsFoundSingle,
        resultsFoundPlural: this.dataset.labelResultsFoundPlural,
        fuel: this.dataset.labelFuel,
        tcu: this.dataset.labelTcu,
        engineCode: this.dataset.labelEngineCode,
        priceEcu: this.dataset.labelPriceEcu,
        priceTcu: this.dataset.labelPriceTcu,
        pricePackage: this.dataset.labelPricePackage,
        priceSelection: this.dataset.labelPriceSelection,
        packageConfirmation: this.dataset.labelPackageConfirmation,
        vatIncluded: this.dataset.labelVatIncluded,
        installationIncluded: this.dataset.labelInstallationIncluded,
        installationConfirm: this.dataset.labelInstallationConfirm,
        stock: this.dataset.labelStock,
        tuned: this.dataset.labelTuned,
        gain: this.dataset.labelGain,
        ron: this.dataset.labelRon,
        requiredHardware: this.dataset.labelRequiredHardware,
        recommendedHardware: this.dataset.labelRecommendedHardware,
        available: this.dataset.labelAvailable,
        noOptions: this.dataset.labelNoOptions,
        softwareOptions: this.dataset.labelSoftwareOptions,
        selectOption: this.dataset.labelSelectOption,
        selectPackage: this.dataset.labelSelectPackage,
        submitSelection: this.dataset.labelSubmitSelection,
        incompleteSelection: this.dataset.labelIncompleteSelection,
        inquireApplication: this.dataset.labelInquireApplication,
        source: this.dataset.labelSource,
        manualReview: this.dataset.labelManualReview,
        variant: this.dataset.labelVariant,
        noMatch: this.dataset.labelNoMatch,
        noMatchText: this.dataset.labelNoMatchText,
        petrol: this.dataset.labelPetrol,
        diesel: this.dataset.labelDiesel,
        yes: this.dataset.labelYes,
        no: this.dataset.labelNo,
        selected: this.dataset.labelSelected,
        packageEcuIncludes: this.dataset.labelPackageEcuIncludes,
        packageTcuIncludes: this.dataset.labelPackageTcuIncludes,
        packageCombinedIncludes: this.dataset.labelPackageCombinedIncludes,
        packageConfirmationIncludes:
          this.dataset.labelPackageConfirmationIncludes,
        packageEcuContext: this.dataset.labelPackageEcuContext,
        packageTcuContext: this.dataset.labelPackageTcuContext,
        packageCombinedContext: this.dataset.labelPackageCombinedContext,
        packageConfirmationContext:
          this.dataset.labelPackageConfirmationContext,
        hardwareNotIncluded: this.dataset.labelHardwareNotIncluded,
        emailError: this.dataset.labelEmailError,
        resultNextTitle: this.dataset.labelResultNextTitle,
        resultNextText: this.dataset.labelResultNextText,
        preliminaryOffer: this.dataset.labelPreliminaryOffer,
        offerPending: this.dataset.labelOfferPending,
        stickyOption: this.dataset.labelStickyOption,
        stickyPackage: this.dataset.labelStickyPackage,
        stickyOffer: this.dataset.labelStickyOffer,
        stickyManual: this.dataset.labelStickyManual,
        formSubmitting: this.dataset.labelFormSubmitting,
      };
    }

    trackConfiguratorStart() {
      if (this.configuratorStarted || !this.data) {
        return;
      }
      this.configuratorStarted = true;
      this.publish("boosthaus_oem_configurator_start", {
        event_source: "oem_configurator",
        device_context: this.deviceContext(),
      });
    }

    async loadData({ restore = false } = {}) {
      if (this.data) {
        if (restore) {
          this.restoreFromUrl();
        }
        return;
      }
      if (this.loadingPromise) {
        return this.loadingPromise;
      }

      if (this.retryButton) {
        this.retryButton.disabled = true;
        this.retryButton.hidden = true;
      }
      this.fieldsContainer?.setAttribute("aria-busy", "true");
      this.setStatus(this.labels.loading, "loading");

      this.loadingPromise = (async () => {
        try {
          const data = await requestData(this.dataset.dataUrl);
          this.validateData(data);
          this.data = data;
          this.setHiddenValue(
            "dataVersion",
            `${data.schemaVersion} / ${data.generatedDate}`,
          );
          this.populateSelect(
            "make",
            uniqueSorted(
              data.applications.map((application) => application.make),
            ),
          );
          this.selects.make.disabled = false;
          if (this.retryButton) {
            this.retryButton.hidden = true;
          }
          if (this.loadFallback) {
            this.loadFallback.hidden = true;
          }
          this.fieldsContainer?.setAttribute("aria-busy", "false");
          this.setStatus(this.labels.ready, "ready");
          this.updateViberLinks();
          this.updateStickyCta();

          if (restore) {
            this.restoreFromUrl();
          }
        } catch (error) {
          DATA_REQUESTS.delete(this.dataset.dataUrl);
          this.fieldsContainer?.setAttribute("aria-busy", "false");
          if (this.retryButton) {
            this.retryButton.disabled = false;
            this.retryButton.hidden = false;
            this.retryButton.textContent = this.labels.retry;
          }
          if (this.loadFallback) {
            this.loadFallback.hidden = false;
          }
          this.setStatus(this.labels.loadError, "error");
          this.publish("boosthaus_oem_data_error", {
            event_source: "oem_configurator",
            device_context: this.deviceContext(),
          });
          this.updateStickyCta();
          console.error(error);
        } finally {
          this.loadingPromise = null;
        }
      })();

      return this.loadingPromise;
    }

    validateData(data) {
      const approvedOrder = [
        "make",
        "model",
        "generation",
        "year",
        "engine_name",
        "transmission_or_tcu",
      ];
      if (
        !data ||
        data.schemaVersion !== "1.1.0" ||
        data.currency !== "EUR" ||
        data.pricesIncludeVat !== true ||
        JSON.stringify(data.selectorOrder) !== JSON.stringify(approvedOrder) ||
        !data.fallbackCopy?.missingPerformance ||
        !data.fallbackCopy?.missingPrice ||
        !data.priceBands ||
        typeof data.priceBands !== "object" ||
        !Array.isArray(data.applications) ||
        data.applications.length === 0 ||
        "tuneOptions" in data ||
        "specialPackages" in data
      ) {
        throw new Error(
          "The OEM+ production dataset has an unsupported shape.",
        );
      }
    }

    setStatus(message, state) {
      this.dataset.loadState = state;
      if (this.statusText) {
        this.statusText.textContent = message;
      }
      if (this.status) {
        this.status.dataset.state = state;
      }
    }

    populateSelect(key, entries, labelResolver) {
      const select = this.selects[key];
      if (!select) {
        return;
      }

      const placeholder = new Option(
        select.dataset.placeholder || this.labels.choose,
        "",
      );
      select.replaceChildren(placeholder);

      for (const entry of entries) {
        const label = labelResolver ? labelResolver(entry) : entry;
        select.add(new Option(label, entry));
      }

      select.disabled = entries.length === 0;
      select.value = "";
    }

    resetAfter(key) {
      const index = SELECT_ORDER.indexOf(key);
      for (const downstreamKey of SELECT_ORDER.slice(index + 1)) {
        this.state[downstreamKey] = "";
        const select = this.selects[downstreamKey];
        if (select) {
          const placeholder = new Option(
            select.dataset.placeholder || this.labels.choose,
            "",
          );
          select.replaceChildren(placeholder);
          select.disabled = true;
        }
      }
      if (index < SELECT_ORDER.indexOf("generation")) {
        this.hideConditionalField("generation");
      }
      if (index < SELECT_ORDER.indexOf("tcu")) {
        this.hideConditionalField("tcu");
      }
      this.hideResults();
      this.resetResultSelection();
    }

    handleChange(key, { updateUrl = true } = {}) {
      this.state[key] = this.selects[key].value;
      this.resetAfter(key);
      this.state[key] = this.selects[key].value;
      this.clearButton.hidden = !this.state.make;

      if (this.state[key]) {
        if (key === "make") {
          const models = uniqueSorted(
            this.data.applications
              .filter(
                (application) => application.make === this.state.make,
              )
              .map((application) => application.model),
          );
          this.populateSelect("model", models);
        } else if (key === "model") {
          this.prepareGenerationOrYear();
        } else if (key === "generation") {
          this.prepareYear();
        } else if (key === "year") {
          this.prepareEngine();
        } else if (key === "engineName") {
          this.prepareTcu();
          this.renderResults();
        } else if (key === "tcu") {
          this.renderResults();
        }
      }

      if (updateUrl && !this.restoring) {
        this.updateUrl();
      }
      this.updateProgress();
      if (this.state[key] && !this.restoring) {
        this.revealNextControl(key);
      }
    }

    applicationsAtStage(stage) {
      return this.data.applications.filter((application) => {
        if (this.state.make && application.make !== this.state.make) {
          return false;
        }
        if (this.state.model && application.model !== this.state.model) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >=
            SELECT_ORDER.indexOf("generation") &&
          this.fields.generation &&
          !this.fields.generation.hidden &&
          this.state.generation &&
          this.valueOrEmpty(application.generation) !== this.state.generation
        ) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >= SELECT_ORDER.indexOf("year") &&
          this.state.year &&
          this.valueOrEmpty(application.year?.raw) !== this.state.year
        ) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >=
            SELECT_ORDER.indexOf("engineName") &&
          this.state.engineName &&
          this.valueOrEmpty(application.engineName) !== this.state.engineName
        ) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >= SELECT_ORDER.indexOf("tcu") &&
          this.state.tcu &&
          !(application.supportedTcuTypes ?? []).includes(this.state.tcu)
        ) {
          return false;
        }
        return true;
      });
    }

    prepareGenerationOrYear() {
      const candidates = this.applicationsAtStage("model");
      const generations = uniqueSorted(
        candidates.map((application) =>
          this.valueOrEmpty(application.generation),
        ),
      );
      const hasNamedGeneration = generations.some(
        (value) => value !== EMPTY_VALUE,
      );

      if (hasNamedGeneration) {
        this.showConditionalField("generation");
        this.populateSelect("generation", generations, (value) =>
          value === EMPTY_VALUE
            ? this.labels.unspecifiedGeneration
            : value,
        );
      } else {
        this.hideConditionalField("generation");
        this.prepareYear();
      }
    }

    prepareYear() {
      const candidates = this.applicationsAtStage("year");
      const years = uniqueSorted(
        candidates.map((application) =>
          this.valueOrEmpty(application.year?.raw),
        ),
      );
      this.populateSelect("year", years, (value) =>
        value === EMPTY_VALUE ? this.labels.unspecifiedYear : value,
      );
    }

    prepareEngine() {
      const candidates = this.applicationsAtStage("engineName");
      const engines = uniqueSorted(
        candidates.map((application) =>
          this.valueOrEmpty(application.engineName),
        ),
      );
      this.populateSelect("engineName", engines, (value) =>
        value === EMPTY_VALUE ? this.labels.unspecifiedEngine : value,
      );
    }

    prepareTcu() {
      const candidates = this.applicationsAtStage("engineName");
      const tcuTypes = uniqueSorted(
        candidates.flatMap(
          (application) => application.supportedTcuTypes ?? [],
        ),
      );
      if (tcuTypes.length > 0) {
        this.showConditionalField("tcu");
        this.populateSelect("tcu", tcuTypes);
        this.selects.tcu.options[0].textContent = this.labels.uncertainTcu;
      } else {
        this.hideConditionalField("tcu");
      }
    }

    showConditionalField(key) {
      if (this.fields[key]) {
        this.fields[key].hidden = false;
      }
    }

    hideConditionalField(key) {
      if (this.fields[key]) {
        this.fields[key].hidden = true;
      }
      if (this.selects[key]) {
        this.selects[key].disabled = true;
        this.selects[key].value = "";
      }
      this.state[key] = "";
    }

    valueOrEmpty(value) {
      return value === undefined || value === null || value === ""
        ? EMPTY_VALUE
        : String(value);
    }

    renderResults() {
      const applications = this.applicationsAtStage("tcu");
      this.resultsGrid.replaceChildren();
      this.empty.hidden = applications.length > 0;
      this.results.hidden = applications.length === 0;

      if (applications.length === 0) {
        this.emptyTitle.textContent = this.labels.noMatch;
        this.emptyText.textContent = this.labels.noMatchText;
        this.setStatus(this.labels.noMatch, "ready");
        const noMatchKey = [
          this.state.make,
          this.state.model,
          this.state.generation,
          this.state.year,
          this.state.engineName,
          this.state.tcu,
          "no-match",
        ].join("|");
        if (noMatchKey !== this.lastTrackedResult) {
          this.lastTrackedResult = noMatchKey;
          this.publish("boosthaus_oem_no_exact_match", {
            event_source: "oem_configurator",
            device_context: this.deviceContext(),
          });
        }
        this.updateProgress();
        this.updateViberLinks();
        this.updateStickyCta();
        return;
      }

      const resultLabel =
        applications.length === 1
          ? this.labels.resultSingular
          : this.labels.resultPlural;
      this.resultsTitle.textContent =
        applications.length === 1
          ? this.labels.resultsFoundSingle
          : this.labels.resultsFoundPlural;
      applications.forEach((application, index) => {
        this.resultsGrid.append(
          this.buildApplicationCard(
            application,
            index + 1,
            applications.length,
          ),
        );
      });
      this.setStatus(`${applications.length} ${resultLabel}`, "ready");
      this.updateProgress();

      const vehicleKey = [
        this.state.make,
        this.state.model,
        this.state.generation,
        this.state.year,
        this.state.engineName,
      ].join("|");
      if (vehicleKey !== this.lastTrackedVehicle) {
        this.lastTrackedVehicle = vehicleKey;
        this.publish("boosthaus_oem_vehicle_selected", {
          event_source: "oem_configurator",
          result_count: applications.length,
          application_id:
            applications.length === 1 ? applications[0].id : undefined,
          device_context: this.deviceContext(),
        });
      }
      if (vehicleKey !== this.lastTrackedResult) {
        this.lastTrackedResult = vehicleKey;
        this.publish("boosthaus_oem_matching_result_displayed", {
          event_source: "oem_configurator",
          result_count: applications.length,
          application_id:
            applications.length === 1 ? applications[0].id : undefined,
          device_context: this.deviceContext(),
        });
      }
      this.updateViberLinks();
      this.updateStickyCta();
    }

    buildApplicationCard(application, resultIndex, resultCount) {
      const card = createElement("article", "bh-oem-result-card");
      card.dataset.applicationId = application.id;

      const header = createElement("div", "bh-oem-result-card__header");
      const headingWrap = createElement("div");
      const eyebrowText = [
        application.make,
        application.fuelType
          ? this.translateSourceValue(application.fuelType)
          : null,
      ]
        .filter(Boolean)
        .join(" • ");
      headingWrap.append(
        createElement("p", "bh-oem-result-card__eyebrow", eyebrowText),
        createElement(
          "h4",
          "bh-oem-result-card__title",
          application.modelDisplay ??
            [application.model, application.generation]
              .filter(Boolean)
              .join(" "),
        ),
      );
      header.append(headingWrap);

      const badges = createElement("div", "bh-oem-result-card__badges");
      if (resultCount > 1) {
        badges.append(
          createElement(
            "span",
            "bh-oem-badge",
            `${this.labels.variant} ${resultIndex}`,
          ),
        );
      }
      if (this.needsManualReview(application)) {
        badges.append(
          createElement(
            "span",
            "bh-oem-badge bh-oem-badge--review",
            this.labels.manualReview,
          ),
        );
      }
      if (badges.childElementCount > 0) {
        header.append(badges);
      }
      card.append(header);

      const meta = createElement("dl", "bh-oem-meta");
      this.appendDefinition(
        meta,
        this.labels.fuel,
        this.translateSourceValue(application.fuelType) || this.labels.unknown,
      );
      this.appendDefinition(
        meta,
        this.labels.engineCode,
        application.engineCode || this.labels.unknown,
      );
      this.appendDefinition(
        meta,
        this.labels.tcu,
        (application.supportedTcuTypes ?? []).join(" / ") ||
          this.labels.unknown,
      );
      card.append(meta);

      card.append(
        createElement(
          "p",
          "bh-oem-result-card__vehicle",
          [
            application.year?.raw || this.labels.unspecifiedYear,
            application.engineName || this.labels.unspecifiedEngine,
          ]
            .filter(Boolean)
            .join(" • "),
        ),
      );

      const notes = [
        application.unlockRequirement,
        application.tcuRecommendation,
        application.requiredHardware,
        application.recommendedHardware,
        application.applicationNotes,
      ].filter(Boolean);
      if (notes.length > 0) {
        const noteBox = createElement(
          "div",
          "bh-oem-result-card__notes",
        );
        for (const note of notes) {
          noteBox.append(
            createElement("p", null, this.translateSourceValue(note)),
          );
        }
        card.append(noteBox);
      }

      const offerSummary = createElement(
        "div",
        "bh-oem-offer-summary",
      );
      const offerCopy = createElement(
        "div",
        "bh-oem-offer-summary__copy",
      );
      offerCopy.append(
        createElement(
          "strong",
          "bh-oem-offer-summary__title",
          this.labels.preliminaryOffer,
        ),
        createElement(
          "span",
          "bh-oem-offer-summary__value",
          this.labels.offerPending,
        ),
      );
      offerSummary.dataset.applicationId = application.id;
      offerSummary.dataset.bhOemCardOffer = application.id;
      const submitSelection = createElement(
        "button",
        "bh-oem-button bh-oem-button--primary",
        this.labels.submitSelection,
      );
      submitSelection.type = "button";
      submitSelection.disabled = true;
      submitSelection.dataset.bhOemCardSubmit = application.id;
      submitSelection.addEventListener("click", () =>
        this.commitLeadSelection(application),
      );
      offerSummary.append(offerCopy, submitSelection);
      card.append(offerSummary);

      const optionsSection = createElement("div", "bh-oem-options");
      optionsSection.append(
        createElement(
          "p",
          "bh-oem-choice-heading",
          this.labels.softwareOptions,
        ),
      );
      const optionGroup = createElement("div", "bh-oem-options__group");
      optionGroup.setAttribute("role", "radiogroup");
      optionGroup.setAttribute("aria-label", this.labels.softwareOptions);
      if (application.options.length > 0) {
        for (const option of application.options) {
          optionGroup.append(this.buildOptionButton(application, option));
        }
      } else {
        optionGroup.append(
          createElement(
            "p",
            "bh-oem-options__empty",
            this.labels.noOptions,
          ),
        );
      }
      optionsSection.append(optionGroup);
      card.append(optionsSection);

      card.append(this.buildPackageChoices(application));

      const nextStep = createElement("div", "bh-oem-result-next");
      nextStep.append(
        createElement(
          "strong",
          "bh-oem-result-next__title",
          this.labels.resultNextTitle,
        ),
        createElement("p", null, this.labels.resultNextText),
      );
      card.append(nextStep);

      const footer = createElement("div", "bh-oem-result-card__footer");
      const manualButton = createElement(
        "button",
        "bh-oem-text-button bh-oem-text-button--inline",
        this.labels.inquireApplication,
      );
      manualButton.type = "button";
      manualButton.addEventListener("click", () =>
        this.selectManualInquiry(application),
      );
      footer.append(manualButton);

      if (this.showSource && application.ecuSourceUrl) {
        const sourceLink = createElement(
          "a",
          "bh-oem-source-link",
          this.labels.source,
        );
        sourceLink.href = application.ecuSourceUrl;
        sourceLink.target = "_blank";
        sourceLink.rel = "nofollow noopener";
        footer.append(sourceLink);
      }
      card.append(footer);

      return card;
    }

    buildOptionButton(application, option) {
      const button = createElement("button", "bh-oem-option");
      button.type = "button";
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", "false");
      button.dataset.applicationId = application.id;
      button.dataset.optionKey = option.key;

      const top = createElement("span", "bh-oem-option__top");
      top.append(
        createElement("strong", "bh-oem-option__title", option.label),
        createElement(
          "span",
          "bh-oem-badge bh-oem-badge--available",
          this.labels.available,
        ),
      );
      button.append(top);

      if (option.fuelRon?.length) {
        button.append(
          createElement(
            "span",
            "bh-oem-option__facts",
            `${this.labels.ron}: ${option.fuelRon.join(" / ")}`,
          ),
        );
      }

      if (this.showOutputs) {
        if (application.factory && option.software) {
          button.append(
            this.buildPerformanceComparison(
              application.factory,
              option.software,
            ),
          );
        } else {
          button.append(
            createElement(
              "span",
              "bh-oem-performance-fallback",
              this.data.fallbackCopy.missingPerformance,
            ),
          );
        }
      }

      if (option.requiredHardware) {
        button.append(
          createElement(
            "span",
            "bh-oem-option__detail",
            `${this.labels.requiredHardware}: ${this.translateSourceValue(
              option.requiredHardware,
            )}`,
          ),
        );
      }
      if (option.recommendedHardware) {
        button.append(
          createElement(
            "span",
            "bh-oem-option__detail",
            `${this.labels.recommendedHardware}: ${this.translateSourceValue(
              option.recommendedHardware,
            )}`,
          ),
        );
      }
      if (option.notes) {
        button.append(
          createElement("span", "bh-oem-option__detail", option.notes),
        );
      }
      button.append(
        createElement(
          "span",
          "bh-oem-option__action",
          this.labels.selectOption,
        ),
      );
      button.addEventListener("click", () =>
        this.selectOption(application, option, button),
      );
      return button;
    }

    buildPerformanceComparison(factory, software) {
      const comparison = createElement(
        "span",
        "bh-oem-performance-comparison",
      );
      comparison.append(
        this.buildPerformanceValue(this.labels.stock, factory.ps, factory.nm),
        this.buildPerformanceValue(
          this.labels.tuned,
          software.ps,
          software.nm,
        ),
        this.buildPerformanceValue(
          this.labels.gain,
          software.ps - factory.ps,
          software.nm - factory.nm,
          true,
        ),
      );
      return comparison;
    }

    buildPerformanceValue(label, ps, nm, signed = false) {
      const item = createElement("span", "bh-oem-performance-value");
      item.append(
        createElement("span", "bh-oem-performance-value__label", label),
        createElement(
          "strong",
          "bh-oem-performance-value__number",
          `${this.formatPerformanceNumber(ps, signed)} PS`,
        ),
        createElement(
          "span",
          "bh-oem-performance-value__torque",
          `${this.formatPerformanceNumber(nm, signed)} Nm`,
        ),
      );
      return item;
    }

    formatPerformanceNumber(value, signed) {
      if (!signed || value <= 0) {
        return String(value);
      }
      return `+${value}`;
    }

    buildPackageChoices(application) {
      const section = createElement("div", "bh-oem-packages");
      section.append(
        createElement(
          "p",
          "bh-oem-choice-heading",
          this.labels.priceSelection,
        ),
      );
      const group = createElement("div", "bh-oem-prices");
      group.setAttribute("role", "radiogroup");
      group.setAttribute("aria-label", this.labels.priceSelection);

      const choices = this.packageChoices(application);
      for (const choice of choices) {
        const button = createElement("button", "bh-oem-price");
        button.type = "button";
        button.setAttribute("role", "radio");
        button.setAttribute("aria-checked", "false");
        button.dataset.applicationId = application.id;
        button.dataset.packageType = choice.type;
        button.append(
          createElement("span", "bh-oem-price__label", choice.label),
          createElement(
            "span",
            "bh-oem-price__includes",
            choice.includes,
          ),
          createElement(
            "span",
            "bh-oem-price__context",
            choice.context,
          ),
        );

        if (choice.band && this.showPrices) {
          button.append(
            createElement(
              "strong",
              "bh-oem-price__value",
              this.formatMoney(choice.band.priceEurVatIncl),
            ),
          );
          const installationCopy =
            choice.band.installationIncluded === true
              ? this.labels.installationIncluded
              : this.labels.installationConfirm;
          button.append(
            createElement(
              "span",
              "bh-oem-price__meta",
              `${this.labels.vatIncluded} • ${installationCopy}`,
            ),
          );
        } else if (!choice.band) {
          button.append(
            createElement(
              "strong",
              "bh-oem-price__value bh-oem-price__value--fallback",
              this.data.fallbackCopy.missingPrice,
            ),
          );
        }

        button.append(
          createElement(
            "span",
            "bh-oem-price__action",
            this.labels.selectPackage,
          ),
        );
        button.addEventListener("click", () =>
          this.selectPackage(application, choice, button),
        );
        group.append(button);
      }
      section.append(group);
      section.append(
        createElement(
          "p",
          "bh-oem-prices__note",
          this.labels.hardwareNotIncluded,
        ),
      );
      return section;
    }

    packageChoices(application) {
      const choices = [];
      for (const [type, labelKey] of PACKAGE_TYPES) {
        const priceKey = application.priceKeys?.[type];
        if (!priceKey) {
          continue;
        }
        const band = this.data.priceBands[priceKey] ?? null;
        choices.push({
          type,
          label: this.labels[labelKey],
          includes:
            type === "ecu"
              ? this.labels.packageEcuIncludes
              : type === "tcu"
                ? this.labels.packageTcuIncludes
                : this.labels.packageCombinedIncludes,
          context:
            type === "ecu"
              ? this.labels.packageEcuContext
              : type === "tcu"
                ? this.labels.packageTcuContext
                : this.labels.packageCombinedContext,
          priceKey,
          band:
            band &&
            typeof band.priceEurVatIncl === "number" &&
            band.priceEurVatIncl > 0
              ? band
              : null,
        });
      }

      if (choices.length === 0) {
        choices.push({
          type: "confirmation",
          label: this.labels.packageConfirmation,
          includes: this.labels.packageConfirmationIncludes,
          context: this.labels.packageConfirmationContext,
          priceKey: "",
          band: null,
        });
      }
      return choices;
    }

    selectOption(application, option, control, { updateUrl = true } = {}) {
      this.ensureApplicationContext(application);
      this.querySelectorAll(".bh-oem-option").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-option__action");
        if (action) {
          action.textContent = this.labels.selectOption;
        }
      });
      control.setAttribute("aria-checked", "true");
      const selectedAction = control.querySelector(".bh-oem-option__action");
      if (selectedAction) {
        selectedAction.textContent = this.labels.selected;
      }
      this.selected.optionKey = option.key;
      this.updateLeadSelection();
      if (updateUrl && !this.restoring) {
        this.updateUrl();
        this.publish("boosthaus_oem_option_selected", {
          event_source: "oem_configurator",
          application_id: application.id,
          option_key: option.key,
          device_context: this.deviceContext(),
        });
      }
      this.updateProgress();
      this.revealElement(
        control.closest(".bh-oem-result-card")?.querySelector(
          ".bh-oem-packages",
        ),
      );
    }

    selectPackage(
      application,
      choice,
      control,
      { updateUrl = true } = {},
    ) {
      this.ensureApplicationContext(application);
      this.querySelectorAll(".bh-oem-price").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-price__action");
        if (action) {
          action.textContent = this.labels.selectPackage;
        }
      });
      control.setAttribute("aria-checked", "true");
      const selectedAction = control.querySelector(".bh-oem-price__action");
      if (selectedAction) {
        selectedAction.textContent = this.labels.selected;
      }
      this.selected.packageType = choice.type;
      this.selected.priceKey = choice.priceKey;
      this.updateLeadSelection();
      if (updateUrl && !this.restoring) {
        this.updateUrl();
        this.publish("boosthaus_oem_package_selected", {
          event_source: "oem_configurator",
          application_id: application.id,
          package_type: choice.type,
          price_available: Boolean(choice.band),
          price_eur: choice.band?.priceEurVatIncl,
          device_context: this.deviceContext(),
        });
      }
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
    }

    ensureApplicationContext(application) {
      if (this.selected.applicationId === application.id) {
        return;
      }
      this.selected = {
        applicationId: application.id,
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.querySelectorAll(".bh-oem-option").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-option__action");
        if (action) {
          action.textContent = this.labels.selectOption;
        }
      });
      this.querySelectorAll(".bh-oem-price").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-price__action");
        if (action) {
          action.textContent = this.labels.selectPackage;
        }
      });
    }

    updateLeadSelection() {
      const application = this.data.applications.find(
        (candidate) => candidate.id === this.selected.applicationId,
      );
      const option = application?.options.find(
        (candidate) => candidate.key === this.selected.optionKey,
      );
      const packageChoice = application
        ? this.packageChoices(application).find(
            (candidate) =>
              candidate.type === this.selected.packageType &&
              candidate.priceKey === this.selected.priceKey,
          )
        : null;

      if (!application) {
        this.resetLeadSelection();
        return;
      }

      const selectedTcu =
        this.state.tcu ||
        (application.supportedTcuTypes?.length === 1
          ? application.supportedTcuTypes[0]
          : this.labels.uncertainTcu);
      const vehicleSummary = [
        application.make,
        application.modelDisplay ?? application.model,
        application.modelDisplay ? null : application.generation,
        application.year?.raw || this.labels.unspecifiedYear,
        application.engineName || this.labels.unspecifiedEngine,
      ]
        .filter(Boolean)
        .join(" • ");
      const priceSummary = packageChoice
        ? this.showPrices
          ? packageChoice.band
            ? `${this.formatMoney(packageChoice.band.priceEurVatIncl)} • ${
                this.labels.vatIncluded
              }`
            : this.data.fallbackCopy.missingPrice
          : this.data.fallbackCopy.missingPrice
        : "—";
      const installationSummary = packageChoice
        ? packageChoice.band
          ? packageChoice.band.installationIncluded === true
            ? this.labels.installationIncluded
            : this.labels.installationConfirm
          : this.labels.installationConfirm
        : "—";
      const summaryValues = {
        vehicle: vehicleSummary,
        transmission: selectedTcu,
        stage: option?.label || "—",
        package: packageChoice?.label || "—",
        price: priceSummary,
        installation: installationSummary,
      };
      for (const [key, value] of Object.entries(summaryValues)) {
        if (this.summaryFields[key]) {
          this.summaryFields[key].textContent = value;
        }
      }
      this.summary.hidden = true;
      if (this.summaryDetails) {
        this.summaryDetails.hidden = false;
      }
      if (this.editButton) {
        this.editButton.hidden = false;
      }

      const values = {
        dataVersion: `${this.data.schemaVersion} / ${this.data.generatedDate}`,
        applicationId: application.id,
        make: application.make,
        model: application.modelDisplay ?? application.model,
        generation: application.generation ?? "",
        year: application.year?.raw ?? "",
        engineName: application.engineName ?? "",
        tcu: selectedTcu,
        optionKey: option?.key ?? "",
        option: option?.label ?? "",
        optionCategory: option?.category ?? "",
        package: packageChoice?.label ?? "",
        priceKey: packageChoice?.priceKey ?? "",
        priceEur:
          packageChoice?.band?.priceEurVatIncl !== undefined
            ? `${packageChoice.band.priceEurVatIncl} EUR`
            : "",
        installationIncluded:
          packageChoice?.band?.installationIncluded === true
            ? this.labels.yes
            : "",
      };
      for (const [key, value] of Object.entries(values)) {
        this.setHiddenValue(key, value);
      }

      const selectionComplete = Boolean(option && packageChoice);
      this.querySelectorAll("[data-bh-oem-card-submit]").forEach((button) => {
        button.disabled =
          button.dataset.bhOemCardSubmit !== application.id ||
          !selectionComplete;
      });
      this.querySelectorAll("[data-bh-oem-card-offer]").forEach((offer) => {
        const value = offer.querySelector(".bh-oem-offer-summary__value");
        if (!value) {
          return;
        }
        value.textContent =
          offer.dataset.bhOemCardOffer === application.id
            ? [
                option?.label,
                packageChoice?.label,
                packageChoice?.band
                  ? this.formatMoney(packageChoice.band.priceEurVatIncl)
                  : packageChoice
                    ? this.data.fallbackCopy.missingPrice
                    : null,
              ]
                .filter(Boolean)
                .join(" • ") || this.labels.offerPending
            : this.labels.offerPending;
      });
      if (!selectionComplete) {
        this.setStatus(this.labels.incompleteSelection, "ready");
      }
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
    }

    commitLeadSelection(application) {
      const selectionComplete =
        this.selected.applicationId === application.id &&
        this.selected.optionKey &&
        this.selected.packageType;
      if (!selectionComplete) {
        this.setStatus(this.labels.incompleteSelection, "ready");
        return;
      }
      this.publish("boosthaus_oem_lead_open", {
        event_source: "oem_configurator",
        cta_location: "result",
        ...this.analyticsSelection(),
      });
      this.publish("boosthaus_oem_primary_offer_click", {
        event_source: "oem_configurator",
        cta_location: "result",
        ...this.analyticsSelection(),
      });
      this.scrollToForm();
    }

    selectManualInquiry(application = null) {
      if (application) {
        this.selected = {
          applicationId: application.id,
          optionKey: "",
          packageType: "",
          priceKey: "",
        };
        this.updateLeadSelection();
      } else {
        this.selected = {
          applicationId: "",
          optionKey: "",
          packageType: "",
          priceKey: "",
        };
        const manualVehicle = [
          this.state.make,
          this.state.model,
          this.state.generation,
          this.state.year,
          this.state.engineName,
          this.state.tcu,
        ]
          .filter(Boolean)
          .join(" • ");
        this.summary.hidden = false;
        this.summary.textContent =
          manualVehicle || this.summary.dataset.defaultText;
        if (this.summaryDetails) {
          this.summaryDetails.hidden = true;
        }
        if (this.editButton) {
          this.editButton.hidden = !manualVehicle;
        }
        const manualValues = {
          make: this.state.make,
          model: this.state.model,
          generation: this.state.generation,
          year: this.state.year,
          engineName: this.state.engineName,
          tcu: this.state.tcu,
        };
        for (const [key, value] of Object.entries(manualValues)) {
          this.setHiddenValue(key, value);
        }
      }
      this.setStatus(this.labels.ready, "ready");
      this.updateUrl();
      this.publish("boosthaus_oem_manual_enquiry", {
        event_source: "oem_configurator",
        application_id: application?.id,
        device_context: this.deviceContext(),
      });
      this.updateViberLinks();
      this.updateStickyCta();
      this.scrollToForm();
    }

    scrollToForm() {
      this.formSection?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    }

    editSelection() {
      this.selectorSection?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
      window.setTimeout(() => {
        this.selects.make?.focus({ preventScroll: true });
      }, 220);
    }

    selectionContext() {
      const application = this.data?.applications.find(
        (candidate) => candidate.id === this.selected.applicationId,
      );
      const option = application?.options.find(
        (candidate) => candidate.key === this.selected.optionKey,
      );
      const packageChoice = application
        ? this.packageChoices(application).find(
            (candidate) =>
              candidate.type === this.selected.packageType &&
              candidate.priceKey === this.selected.priceKey,
          )
        : null;
      return { application, option, packageChoice };
    }

    analyticsSelection() {
      const { application, option, packageChoice } =
        this.selectionContext();
      return {
        application_id: application?.id || undefined,
        option_key: option?.key || undefined,
        stage: option?.label || undefined,
        package_type: packageChoice?.type || undefined,
        price_available: Boolean(packageChoice?.band),
        price_eur: packageChoice?.band?.priceEurVatIncl,
        device_context: this.deviceContext(),
      };
    }

    deviceContext() {
      return window.matchMedia("(max-width: 749px)").matches
        ? "mobile"
        : "desktop";
    }

    continueJourney() {
      if (!this.data) {
        this.selectorSection?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
          block: "start",
        });
        this.loadData({ restore: this.hasRestorableUrlState() });
        return;
      }

      const { application, option, packageChoice } =
        this.selectionContext();
      if (application && option && packageChoice) {
        this.commitLeadSelection(application);
        return;
      }

      if (!this.results.hidden) {
        const card =
          this.querySelector(
            `[data-application-id="${this.selected.applicationId}"]`,
          ) || this.resultsGrid.querySelector(".bh-oem-result-card");
        const target = option
          ? card?.querySelector(".bh-oem-packages")
          : card?.querySelector(".bh-oem-options");
        this.scrollToElement(target || this.results);
        return;
      }

      if (!this.empty.hidden) {
        this.selectManualInquiry();
        return;
      }

      this.scrollToElement(this.selectorSection);
      this.selects.make?.focus({ preventScroll: true });
    }

    scrollToElement(element) {
      element?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    }

    handleStickyAction() {
      const { application } = this.selectionContext();
      if (this.stickyState === "offer" && application) {
        this.publish("boosthaus_oem_primary_offer_click", {
          event_source: "oem_configurator",
          cta_location: "mobile_sticky",
          ...this.analyticsSelection(),
        });
        this.scrollToForm();
        return;
      }
      if (this.stickyState === "manual") {
        this.selectManualInquiry(application);
        return;
      }
      this.continueJourney();
    }

    updateStickyCta() {
      if (!this.sticky || !this.stickyAction) {
        return;
      }
      if (
        window.innerWidth >= 750 ||
        this.formInView ||
        this.form?.dataset.formState === "success"
      ) {
        this.sticky.hidden = true;
        return;
      }

      const { application, option, packageChoice } =
        this.selectionContext();
      let state = "";
      let label = "";
      if (this.dataset.loadState === "error" || !this.empty.hidden) {
        state = "manual";
        label = this.labels.stickyManual;
      } else if (!this.results.hidden) {
        if (!option) {
          state = "option";
          label = this.labels.stickyOption;
        } else if (!packageChoice) {
          state = "package";
          label = this.labels.stickyPackage;
        } else {
          state = "offer";
          label = this.labels.stickyOffer;
        }
      }

      if (!state) {
        this.sticky.hidden = true;
        return;
      }

      const vehicleLabel = [
        application?.make || this.state.make,
        application?.modelDisplay ||
          application?.model ||
          this.state.model,
      ]
        .filter(Boolean)
        .join(" ");
      const detailLabel = [
        option?.label,
        packageChoice?.label,
        packageChoice?.band
          ? this.formatMoney(packageChoice.band.priceEurVatIncl)
          : null,
      ]
        .filter(Boolean)
        .join(" • ");
      this.stickyState = state;
      this.sticky.dataset.state = state;
      this.stickyAction.textContent = label;
      if (this.stickySummary) {
        this.stickySummary.textContent =
          vehicleLabel || this.labels.stickyManual;
      }
      if (this.stickyDetail) {
        this.stickyDetail.textContent =
          detailLabel || this.labels.resultNextText;
      }
      this.sticky.hidden = false;
    }

    buildViberMessage() {
      const { application, option, packageChoice } =
        this.selectionContext();
      const shorten = (value) => {
        const text = String(value || "").trim();
        return text.length > 21 ? `${text.slice(0, 20)}…` : text;
      };
      const parts = [
        [
          application?.make || this.state.make,
          application?.modelDisplay ||
            application?.model ||
            this.state.model,
        ]
          .filter(Boolean)
          .join(" "),
        [
          application?.generation || this.state.generation,
          application?.year?.raw || this.state.year,
        ]
          .filter(Boolean)
          .join("/"),
        application?.engineName || this.state.engineName,
        this.state.tcu ||
          (application?.supportedTcuTypes?.length === 1
            ? application.supportedTcuTypes[0]
            : ""),
        [option?.label, packageChoice?.label].filter(Boolean).join("/"),
        packageChoice?.band
          ? this.formatMoney(packageChoice.band.priceEurVatIncl)
          : "",
      ]
        .filter(Boolean)
        .map(shorten);

      if (parts.length === 0) {
        return "Здравейте! Искам OEM+ оферта. Моля, помогнете ми да потвърдим съвместимостта.";
      }
      return `Здравейте! OEM+ оферта: ${parts.join(
        " | ",
      )}. Потвърдете съвместимостта.`;
    }

    updateViberLinks() {
      const href = `viber://forward?text=${encodeURIComponent(
        this.buildViberMessage(),
      )}`;
      this.querySelectorAll("[data-bh-oem-viber]").forEach((link) => {
        link.href = href;
      });
    }

    setSubmitting(submitting) {
      if (!this.form || !this.formSubmit) {
        return;
      }
      this.form.dataset.submitting = submitting ? "true" : "false";
      this.form.setAttribute("aria-busy", submitting ? "true" : "false");
      this.formSubmit.disabled = submitting;
      this.formSubmit.textContent = submitting
        ? this.labels.formSubmitting
        : this.formSubmitDefaultText;
    }

    updateProgress() {
      const vehicleComplete = Boolean(
        this.state.make &&
          this.state.model &&
          this.state.year &&
          this.state.engineName,
      );
      const softwareComplete = Boolean(
        vehicleComplete && this.selected.optionKey,
      );
      const packageComplete = Boolean(
        softwareComplete && this.selected.packageType,
      );
      const states = {
        vehicle: vehicleComplete ? "complete" : "current",
        software: !vehicleComplete
          ? "upcoming"
          : softwareComplete
            ? "complete"
            : "current",
        package: !softwareComplete
          ? "upcoming"
          : packageComplete
            ? "complete"
            : "current",
      };

      for (const [key, item] of Object.entries(this.progressItems)) {
        const state = states[key] || "upcoming";
        item.dataset.state = state;
        if (state === "current") {
          item.setAttribute("aria-current", "step");
        } else {
          item.removeAttribute("aria-current");
        }
      }
    }

    revealNextControl(key) {
      const currentIndex = SELECT_ORDER.indexOf(key);
      const nextSelect = SELECT_ORDER.slice(currentIndex + 1)
        .map((nextKey) => this.selects[nextKey])
        .find(
          (select) =>
            select &&
            !select.disabled &&
            !select.closest("[hidden]"),
        );
      if (nextSelect) {
        this.revealElement(nextSelect.closest(".bh-oem-field"));
      } else if (!this.results.hidden) {
        this.revealElement(this.results);
      }
    }

    revealElement(element) {
      if (!element || window.innerWidth >= 750 || this.restoring) {
        return;
      }
      const bounds = element.getBoundingClientRect();
      if (bounds.top >= 80 && bounds.bottom <= window.innerHeight - 24) {
        return;
      }
      element.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "nearest",
      });
    }

    captureAttribution() {
      const url = new URL(window.location.href);
      const storedAttribution = {};
      try {
        const storedValue = JSON.parse(
          window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}",
        );
        if (storedValue && typeof storedValue === "object") {
          for (const [key] of ATTRIBUTION_FIELDS) {
            if (typeof storedValue[key] === "string") {
              storedAttribution[key] = storedValue[key];
            }
          }
        }
      } catch {}

      const attribution = Object.fromEntries(
        ATTRIBUTION_FIELDS.map(([key, parameter]) => {
          const urlValue = (url.searchParams.get(parameter) || "").trim();
          if (urlValue) {
            return [key, urlValue];
          }
          if (key === "landingPage") {
            return [key, storedAttribution[key] || url.href];
          }
          return [key, storedAttribution[key] || ""];
        }),
      );

      try {
        window.sessionStorage.setItem(
          ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution),
        );
      } catch {}

      for (const [key, value] of Object.entries(attribution)) {
        this.setHiddenValue(key, value);
      }
    }

    showEmailError() {
      if (!this.emailInput || !this.emailError) {
        return;
      }
      this.emailInput.setAttribute("aria-invalid", "true");
      this.emailError.hidden = false;
      const describedBy = new Set(
        (this.emailInput.getAttribute("aria-describedby") || "")
          .split(/\s+/)
          .filter(Boolean),
      );
      if (this.emailError.id) {
        describedBy.add(this.emailError.id);
      }
      this.emailInput.setAttribute(
        "aria-describedby",
        [...describedBy].join(" "),
      );
    }

    hideEmailError() {
      if (!this.emailInput || !this.emailError) {
        return;
      }
      this.emailInput.removeAttribute("aria-invalid");
      this.emailError.hidden = true;
      const describedBy = (
        this.emailInput.getAttribute("aria-describedby") || ""
      )
        .split(/\s+/)
        .filter((id) => id && id !== this.emailError.id);
      if (describedBy.length > 0) {
        this.emailInput.setAttribute(
          "aria-describedby",
          describedBy.join(" "),
        );
      } else {
        this.emailInput.removeAttribute("aria-describedby");
      }
    }

    publish(name, payload) {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, ...payload });
      }
    }

    async handleFormSubmit(event) {
      event.preventDefault();
      if (
        !this.form ||
        this.form.dataset.submitting === "true" ||
        confirmedSubmissionHandled
      ) {
        return;
      }

      this.hideSubmissionError();
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        if (this.emailInput && !this.emailInput.validity.valid) {
          this.showEmailError();
        }
        return;
      }

      if (this.formSubject) {
        const details = [
          this.hiddenFields.make?.value,
          this.hiddenFields.model?.value,
          this.hiddenFields.option?.value,
        ].filter(Boolean);
        this.formSubject.value = [this.formSubjectDefault, ...details].join(
          " — ",
        );
      }

      this.setSubmitting(true);
      try {
        const response = await fetch(this.form.action, {
          method: "POST",
          body: new FormData(this.form),
          headers: {
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`OEM+ enquiry request failed with ${response.status}`);
        }

        confirmedSubmissionHandled = true;
        this.publish("boosthaus_oem_enquiry_success", {
          event_source: "oem_configurator",
          ...this.analyticsSelection(),
        });
        window.location.assign(SUCCESS_REDIRECT_PATH);
      } catch (error) {
        this.setSubmitting(false);
        this.showSubmissionError();
      }
    }

    showSubmissionError() {
      if (!this.formError) {
        return;
      }
      this.formError.hidden = false;
      this.formError.focus({ preventScroll: true });
    }

    hideSubmissionError() {
      if (this.formError) {
        this.formError.hidden = true;
      }
    }

    resetResultSelection() {
      this.selected = {
        applicationId: "",
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.resetLeadSelection();
    }

    resetLeadSelection() {
      this.summary.hidden = false;
      this.summary.textContent = this.summary.dataset.defaultText;
      if (this.summaryDetails) {
        this.summaryDetails.hidden = true;
      }
      if (this.editButton) {
        this.editButton.hidden = true;
      }
      for (const [key, input] of Object.entries(this.hiddenFields)) {
        if (key === "dataVersion" && this.data) {
          input.value = `${this.data.schemaVersion} / ${this.data.generatedDate}`;
        } else if (
          ![
            "utmSource",
            "utmMedium",
            "utmCampaign",
            "utmContent",
            "utmTerm",
            "placement",
            "fbclid",
            "landingPage",
          ].includes(key)
        ) {
          input.value = "";
        }
      }
      this.querySelectorAll("[data-bh-oem-card-submit]").forEach((button) => {
        button.disabled = true;
      });
      this.querySelectorAll(".bh-oem-offer-summary__value").forEach((value) => {
        value.textContent = this.labels.offerPending;
      });
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
    }

    setHiddenValue(key, value) {
      if (this.hiddenFields[key]) {
        this.hiddenFields[key].value = value;
      }
    }

    appendDefinition(list, term, value) {
      const group = createElement("div", "bh-oem-meta__group");
      group.append(
        createElement("dt", "bh-oem-meta__term", term),
        createElement("dd", "bh-oem-meta__value", value),
      );
      list.append(group);
    }

    translateSourceValue(value) {
      if (typeof value !== "string") {
        return value;
      }
      const normalized = value.trim().toLowerCase();
      if (normalized === "petrol") {
        return this.labels.petrol;
      }
      if (normalized === "diesel") {
        return this.labels.diesel;
      }
      if (normalized === "yes") {
        return this.labels.yes;
      }
      if (normalized === "no") {
        return this.labels.no;
      }
      return value;
    }

    formatMoney(amount) {
      return new Intl.NumberFormat("bg-BG", {
        style: "currency",
        currency: this.data.currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }

    needsManualReview(application) {
      return (
        !application.year?.raw ||
        !application.engineName ||
        (application.reviewFlags ?? []).length > 0
      );
    }

    hideResults() {
      this.results.hidden = true;
      this.empty.hidden = true;
      this.resultsGrid.replaceChildren();
      this.updateStickyCta();
    }

    clearSelection() {
      this.state = Object.fromEntries(SELECT_ORDER.map((key) => [key, ""]));
      for (const [key, select] of Object.entries(this.selects)) {
        if (key === "make") {
          select.value = "";
          select.disabled = !this.data;
        } else {
          const placeholder = new Option(
            select.dataset.placeholder || this.labels.choose,
            "",
          );
          select.replaceChildren(placeholder);
          select.disabled = true;
        }
      }
      this.hideConditionalField("generation");
      this.hideConditionalField("tcu");
      this.hideResults();
      this.resetResultSelection();
      this.clearButton.hidden = true;
      this.setStatus(this.labels.ready, "ready");
      this.updateUrl();
      this.selects.make.focus();
    }

    hasRestorableUrlState() {
      const url = new URL(window.location.href);
      return Object.values(URL_KEYS).some((key) => {
        const value = url.searchParams.get(key);
        return value !== null && value.trim() !== "";
      });
    }

    restoreFromUrl() {
      const url = new URL(window.location.href);
      const requested = Object.fromEntries(
        Object.entries(URL_KEYS).map(([stateKey, parameter]) => [
          stateKey,
          url.searchParams.get(parameter) ?? "",
        ]),
      );
      this.restoring = true;
      let restoredAny = false;
      let invalid = false;

      for (const key of SELECT_ORDER) {
        const requestedValue = requested[key];
        if (!requestedValue) {
          if (key === "generation" && this.fields.generation?.hidden) continue;
          if (key === "tcu") continue;
          break;
        }
        const select = this.selects[key];
        const optionExists = [...select.options].some(
          (option) => option.value === requestedValue,
        );
        if (select.disabled || !optionExists) {
          invalid = true;
          break;
        }
        select.value = requestedValue;
        this.handleChange(key, { updateUrl: false });
        restoredAny = true;
      }

      if (this.state.engineName && requested.application) {
        const applications = this.applicationsAtStage("tcu");
        const application = applications.find(
          (candidate) => candidate.id === requested.application,
        );
        if (application) {
          const option = application.options.find(
            (candidate) => candidate.key === requested.option,
          );
          const packageChoice = this.packageChoices(application).find(
            (candidate) => candidate.type === requested.packageType,
          );
          if (option) {
            const optionControl = [
              ...this.querySelectorAll("[data-option-key]"),
            ].find(
              (control) =>
                control.dataset.applicationId === application.id &&
                control.dataset.optionKey === option.key,
            );
            if (optionControl) {
              this.selectOption(application, option, optionControl, {
                updateUrl: false,
              });
            }
          } else if (requested.option) {
            invalid = true;
          }
          if (packageChoice) {
            const packageControl = [
              ...this.querySelectorAll("[data-package-type]"),
            ].find(
              (control) =>
                control.dataset.applicationId === application.id &&
                control.dataset.packageType === packageChoice.type,
            );
            if (packageControl) {
              this.selectPackage(
                application,
                packageChoice,
                packageControl,
                { updateUrl: false },
              );
            }
          } else if (requested.packageType) {
            invalid = true;
          }
        } else {
          invalid = true;
        }
      }

      this.restoring = false;
      this.clearButton.hidden = !this.state.make;
      this.updateUrl();
      if (invalid) {
        this.setStatus(this.labels.restoreIgnored, "ready");
      } else if (restoredAny) {
        this.setStatus(this.labels.restored, "ready");
      }
    }

    updateUrl() {
      const url = new URL(window.location.href);
      for (const key of SELECT_ORDER) {
        const parameter = URL_KEYS[key];
        const value = this.state[key];
        if (value) {
          url.searchParams.set(parameter, value);
        } else {
          url.searchParams.delete(parameter);
        }
      }

      const selectedValues = {
        application: this.selected.applicationId,
        option: this.selected.optionKey,
        packageType: this.selected.packageType,
      };
      for (const [key, value] of Object.entries(selectedValues)) {
        const parameter = URL_KEYS[key];
        if (value) {
          url.searchParams.set(parameter, value);
        } else {
          url.searchParams.delete(parameter);
        }
      }
      window.history.replaceState({}, "", url);
    }
  }

  if (!customElements.get("bh-oem-configurator")) {
    customElements.define(
      "bh-oem-configurator",
      BoostHausOemConfigurator,
    );
  }
})();
